import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Dropdown } from 'primereact/dropdown';

import { isOrNotConditionOptions } from 'constants/automation';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import useGetEventTypes from 'modules/file_manager/hooks/useGetEventTypes';

const eventTypeCustomTemplate = (item, props) => {
  if (item) {
    const { event_type_icon = '', event_type = '' } = item || {};
    return (
      <div className="d-flex align-items-center">
        {event_type_icon ? (
          <span className="mx-2">
            <i className={event_type_icon} />
          </span>
        ) : null}
        {event_type}
      </div>
    );
  }

  return <span>{props?.placeholder ? props?.placeholder : null}</span>;
};

const EventTypeFilter = ({ index, practiceAreaId = [], typeListOptions }) => {
  const { typeList, isEventTypeLoading } = useGetEventTypes(practiceAreaId);
  const {
    formState: { errors },
    control,
    getValues,
    setValue,
  } = useFormContext();
  const isFirstRender = useRef(true);
  const [optionsVersion, setOptionsVersion] = useState(0);
  const [listVersion, setListVersion] = useState(0);
  const prevTypeListOptions = useRef(typeListOptions);
  const prevTypeList = useRef(typeList);

  useEffect(() => {
    if (typeListOptions !== prevTypeListOptions.current) {
      if (typeListOptions !== undefined && typeListOptions !== null && Array.isArray(typeListOptions)) {
        setOptionsVersion((prev) => prev + 1);
      }
      prevTypeListOptions.current = typeListOptions;
    }
  }, [typeListOptions]);

  useEffect(() => {
    if (typeList !== prevTypeList.current) {
      if (typeList !== undefined && typeList !== null && Array.isArray(typeList)) {
        setListVersion((prev) => prev + 1);
      }
      prevTypeList.current = typeList;
    }
  }, [typeList]);

  const options = useMemo(() => {
    const optionsHasUpdates = optionsVersion > 0;
    const listHasUpdates = listVersion > 0;

    if (optionsHasUpdates && listHasUpdates) {
      return optionsVersion >= listVersion ? typeListOptions || [] : typeList || [];
    } else if (optionsHasUpdates) {
      return typeListOptions || [];
    } else if (listHasUpdates) {
      return typeList || [];
    }
    return typeList || [];
  }, [typeListOptions, typeList, optionsVersion, listVersion]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const currentValue = getValues(`filters.${index}.value`);
    const selectedValue = currentValue?.['event_type.event_type'];

    if (selectedValue && options.length > 0) {
      const validOptionIds = new Set(options.map((option) => option.event_type_id));

      if (!validOptionIds.has(selectedValue)) {
        setValue(`filters.${index}.value`, { 'event_type.event_type': [] }, { shouldValidate: true });
      }
    }
    if (options.length === 0) {
      setValue(`filters.${index}.value`, { 'event_type.event_type': [] }, { shouldValidate: true });
    }
  }, [options, index, getValues, setValue]);

  return (
    <>
      <div className="me-2 filterbar-condition">
        <Controller
          name={`filters.${index}.condition`}
          control={control}
          rules={{ required: 'Condition is required' }}
          render={({ field }) => (
            <Dropdown
              options={isOrNotConditionOptions}
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
              placeholder="Select Condition"
              className="w-100"
              inputRef={field.ref}
            />
          )}
        />
        <RequiredValidation field={`filters.${index}.condition`} errors={errors} />
      </div>
      <div className="me-2 filterbar-value">
        <Controller
          name={`filters.${index}.value`}
          control={control}
          rules={{ required: 'Value is required' }}
          render={({ field }) => (
            <Dropdown
              options={options}
              value={field.value?.['event_type.event_type'] || ''}
              name={field.name}
              onChange={(e) => field.onChange({ 'event_type.event_type': e.value })}
              isFilter={true}
              optionLabel={'event_type'}
              optionValue={'event_type_id'}
              itemTemplate={eventTypeCustomTemplate}
              valueTemplate={eventTypeCustomTemplate}
              className="w-100"
              placeholder="Select"
              dropdownIcon={isEventTypeLoading ? 'pi pi-spin pi-spinner' : undefined}
              inputRef={field.ref}
            />
          )}
        />
        <RequiredValidation field={`filters.${index}.value`} errors={errors} />
      </div>
    </>
  );
};

export default EventTypeFilter;
